import React, { useEffect, useMemo, useState } from "react";
import {
  Box, Typography, Button, Dialog, DialogContent, DialogActions,
  TextField, MenuItem, Autocomplete, Chip, CircularProgress,
} from "@mui/material";
import TitleIcon from "@mui/icons-material/TitleOutlined";
import ScheduleIcon from "@mui/icons-material/ScheduleOutlined";
import PeopleIcon from "@mui/icons-material/PeopleOutline";
import MenuBookIcon from "@mui/icons-material/MenuBookOutlined";
import PaymentsIcon from "@mui/icons-material/PaymentsOutlined";
import NotesIcon from "@mui/icons-material/NotesOutlined";
import RepeatIcon from "@mui/icons-material/RepeatOutlined";
import moment from "moment";
import { create_group_session_api } from "../../../DAL/SchoolPortal/SchoolPortal";
import { propose_session_api } from "../../../DAL/Tutors/Tutors";
import {
  border, textPrimary, textSecondary, textMuted, accent, font,
  inputSx, primaryButtonSx, secondaryButtonSx, dialogPaperSx, formatSubjectName,
} from "../../../layouts/schoolPortal/schoolPortalTheme";

/**
 * WHAT A DRAGGED RANGE TURNS INTO.
 *
 * The calendar used to answer that with a two-item menu — close the time, or
 * reserve it — because those were the only two things availability exceptions
 * could be. Both of those say what CANNOT happen in a slot. Neither of them
 * puts anything in the diary, which is the thing an instructor opens a
 * calendar to do.
 *
 * So this is the composer the drag hands off to, and it covers all four kinds
 * the range can become. The first two create real events; the last two are the
 * old availability exceptions, kept because closing an afternoon is still the
 * fastest thing you do on this screen.
 *
 *   class   → POST /api/school/group-sessions   (many students, one room)
 *   1:1     → POST /api/tutors/students/:id/propose
 *   reserve → an availability slot held for one named student
 *   close   → an availability slot nobody can book
 *
 * THE DRAGGED TIME IS A STARTING POINT, NOT A COMMITMENT. Every field it fills
 * — date, start, end — stays editable here. Dragging 15 minutes off the bottom
 * of an hour is a pointer-accuracy problem, and making somebody redo the drag
 * to fix it is the kind of thing that teaches people to distrust drag entirely.
 *
 * WHO CAN CREATE WHAT. Scheduling a class is an owner/manager action server
 * side (`isManager` in controllers/school/group_sessions.js). An instructor who
 * is not one gets no Class tab rather than a tab that 403s — an option you are
 * allowed to fill in and not allowed to submit is worse than no option.
 */

const KINDS = [
  { value: "class",   label: "Class",        blurb: "Several students, one room" },
  { value: "oneToOne", label: "1:1 session", blurb: "Offer a time to one student" },
  { value: "reserve", label: "Reserve",      blurb: "Hold it for one student" },
  { value: "close",   label: "Close time",   blurb: "Nobody can book it" },
];

/** Left-hand icon rail, so a row is identifiable before it is read. */
const Row = ({ icon, children, align = "center" }) => (
  <Box sx={{ display: "flex", alignItems: align, gap: 1.25, mb: 1.5 }}>
    <Box sx={{ color: textMuted, display: "flex", pt: align === "flex-start" ? "9px" : 0, "& svg": { fontSize: 18 } }}>
      {icon}
    </Box>
    <Box sx={{ flex: 1, minWidth: 0 }}>{children}</Box>
  </Box>
);

const toDateInput = (d) => moment(d).format("YYYY-MM-DD");
const toTimeInput = (d) => moment(d).format("HH:mm");

/** Rebuild a Date from the three inputs, so editing any one of them is enough. */
const combine = (dateStr, timeStr) => moment(`${dateStr} ${timeStr}`, "YYYY-MM-DD HH:mm").toDate();

export default function SessionComposer({
  open,
  onClose,
  range,                 // { start: Date, end: Date } from the drag
  students = [],
  lessons = [],          // from GET /api/school/group-sessions
  defaultPricePerHead = 25,
  instructorId,
  canScheduleClass = false,
  currencySymbol: cur = "$",
  onCreateSlot,          // (start, end, visibility, student) — availability path
  onDone,                // refetch the diary
  notify,                // (message, variant)
  // A consolidation request this proposal answers. When present the composer
  // is locked to a 1:1 for that student — the other three kinds are not what
  // was asked for, and offering them here is how somebody schedules a class
  // against a request for a private session.
  consolidationRequest = null,
}) {
  const firstKind = consolidationRequest ? "oneToOne" : (canScheduleClass ? "class" : "oneToOne");
  const [kind, setKind] = useState(firstKind);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const [title, setTitle] = useState("");
  const [date, setDate] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [lessonNumber, setLessonNumber] = useState("");
  const [subject, setSubject] = useState("");
  const [invitees, setInvitees] = useState([]);
  const [capacity, setCapacity] = useState(8);
  const [price, setPrice] = useState(defaultPricePerHead);
  const [repeatWeeks, setRepeatWeeks] = useState(1);
  const [note, setNote] = useState("");

  // Reset to the dragged range every time the dialog opens. Without the reset
  // the second drag of a session inherits the first one's title and invitees.
  useEffect(() => {
    if (!open || !range) return;
    setKind(firstKind);
    setBusy(false);
    setError(null);
    setDate(toDateInput(range.start));
    setFrom(toTimeInput(range.start));
    setTo(toTimeInput(range.end));
    setLessonNumber("");

    if (consolidationRequest) {
      // Everything the request already told us, filled in. The instructor is
      // answering a question, not composing from nothing, and re-typing the
      // student's own name and lesson is how a two-click action becomes six.
      const s = students.find((x) => String(x._id) === String(consolidationRequest.studentId));
      setTitle(`Lesson ${consolidationRequest.lesson} consolidation`);
      setSubject(consolidationRequest.subjects?.[0] || "");
      setInvitees(s ? [s] : []);
      setNote(
        consolidationRequest.note
          ? `You asked: “${consolidationRequest.note}” — here is a time to go through Lesson ${consolidationRequest.lesson}.`
          : `A time to go through Lesson ${consolidationRequest.lesson} so I can open the next one.`
      );
      setCapacity(8);
      setPrice(defaultPricePerHead);
      setRepeatWeeks(1);
      return;
    }

    setTitle("");
    setSubject("");
    setInvitees([]);
    setCapacity(8);
    setPrice(defaultPricePerHead);
    setRepeatWeeks(1);
    setNote("");
  }, [open, range, firstKind, defaultPricePerHead, consolidationRequest, students]);

  const start = useMemo(() => (date && from ? combine(date, from) : null), [date, from]);
  const end = useMemo(() => (date && to ? combine(date, to) : null), [date, to]);
  const minutes = start && end ? Math.round((end - start) / 60000) : 0;

  const subjectOptions = useMemo(() => {
    const set = new Set(lessons.map((l) => l.subject).filter(Boolean));
    return Array.from(set).sort();
  }, [lessons]);

  // Picking a lesson fills the title and the subject, because a class named
  // after its lesson is what the syllabus already calls it — and the subject
  // key is a foreign key the server validates, not free text worth retyping.
  const pickLesson = (n) => {
    setLessonNumber(n);
    const l = lessons.find((x) => String(x.n) === String(n));
    if (!l) return;
    if (!title.trim()) setTitle(l.title);
    if (l.subject) setSubject(l.subject);
  };

  const validate = () => {
    if (!start || !end) return "Pick a date and a time.";
    if (!(start < end)) return "The end time has to be after the start time.";
    if (kind === "class") {
      if (!instructorId) return "No instructor to schedule this against.";
      if (!lessonNumber) return "Pick which lesson this class covers.";
      if (Number(capacity) < 2) return "A class needs room for at least two.";
      if (Number(capacity) < invitees.length) return `You have invited ${invitees.length} but capacity is ${capacity}.`;
    }
    if (kind === "oneToOne") {
      if (invitees.length !== 1) return "Pick exactly one student for a 1:1.";
      if (!note.trim()) return "Add a note — the student is told why you are offering this.";
    }
    if (kind === "reserve" && invitees.length !== 1) return "Pick the student this is held for.";
    return null;
  };

  const submit = async () => {
    const bad = validate();
    if (bad) { setError(bad); return; }
    setBusy(true);
    setError(null);
    try {
      if (kind === "close") {
        await onCreateSlot(start, end, "blocked");
      } else if (kind === "reserve") {
        await onCreateSlot(start, end, "reserved", invitees[0]);
      } else if (kind === "oneToOne") {
        const r = await propose_session_api(invitees[0]._id, {
          start_time: start.toISOString(),
          end_time: end.toISOString(),
          subject: subject || "",
          note: note.trim(),
          // Carries the lesson onto the booking AND closes the request loop.
          // Without it the session is untagged and only reaches the gate
          // through the "current lesson" fallback — right today, wrong the
          // moment the student is two lessons ahead of what they asked about.
          ...(consolidationRequest ? { consolidation_request_id: consolidationRequest.id } : {}),
        });
        // 201 is the proposal's own success code, not 200.
        if (![200, 201].includes(r?.code)) throw new Error(r?.message || "Could not offer that session");
        notify?.(r.message || "Offered — the student has been asked to confirm", "success");
      } else {
        const r = await create_group_session_api({
          instructorId,
          lessonNumber: Number(lessonNumber),
          title: title.trim(),
          subject: subject || "",
          start_time: start.toISOString(),
          end_time: end.toISOString(),
          capacity: Number(capacity),
          pricePerHead: Number(price),
          repeatWeeks: Number(repeatWeeks) || 1,
          attendees: invitees.map((s) => ({ studentId: s._id, studentType: "flight_school_student" })),
        });
        if (r?.code !== 200) throw new Error(r?.message || "Could not schedule that class");
        notify?.(r.message || "Class scheduled", "success");
      }
      onDone?.();
      onClose();
    } catch (e) {
      setError(e.message || "That did not save.");
    } finally {
      setBusy(false);
    }
  };

  const kinds = consolidationRequest
    ? KINDS.filter((k) => k.value === "oneToOne")
    : KINDS.filter((k) => k.value !== "class" || canScheduleClass);
  const active = KINDS.find((k) => k.value === kind);
  const wantsPeople = kind === "class" || kind === "oneToOne" || kind === "reserve";
  const multi = kind === "class";

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth PaperProps={{ sx: dialogPaperSx }}>
      {/* Kind first. What this is decides which of the fields below mean
          anything, so it cannot sit underneath them. */}
      <Box sx={{ display: "flex", gap: 0.4, p: 1.25, pb: 0, flexWrap: "wrap" }}>
        {kinds.map((k) => (
          <Box
            key={k.value}
            onClick={() => { setKind(k.value); setError(null); }}
            sx={{
              px: 1.25, py: 0.6, borderRadius: "8px", cursor: "pointer", userSelect: "none",
              fontFamily: font, fontSize: "12px", fontWeight: 700,
              color: kind === k.value ? "#fff" : textSecondary,
              backgroundColor: kind === k.value ? accent : "transparent",
              border: `1px solid ${kind === k.value ? accent : border}`,
              transition: "all 0.12s",
              "&:hover": { borderColor: accent, color: kind === k.value ? "#fff" : accent },
            }}
          >
            {k.label}
          </Box>
        ))}
      </Box>
      <Typography sx={{ fontFamily: font, fontSize: "11px", color: textMuted, px: 1.6, pt: 0.75 }}>
        {active?.blurb}
      </Typography>

      <DialogContent sx={{ pt: 1.5, pb: 0.5 }}>
        {kind !== "close" && (
          <Row icon={<TitleIcon />}>
            <TextField
              size="small" fullWidth variant="standard"
              placeholder={kind === "class" ? "Add a title" : "Add a title (optional)"}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              InputProps={{ disableUnderline: true, sx: { fontFamily: font, fontSize: "16px", fontWeight: 700, color: textPrimary } }}
            />
          </Row>
        )}

        <Row icon={<ScheduleIcon />}>
          <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
            <TextField size="small" type="date" value={date} onChange={(e) => setDate(e.target.value)} sx={{ ...inputSx, flex: "1 1 132px" }} />
            <TextField size="small" type="time" value={from} onChange={(e) => setFrom(e.target.value)} sx={{ ...inputSx, width: 104 }} />
            <Typography sx={{ fontFamily: font, fontSize: "12px", color: textMuted }}>→</Typography>
            <TextField size="small" type="time" value={to} onChange={(e) => setTo(e.target.value)} sx={{ ...inputSx, width: 104 }} />
            <Typography sx={{ fontFamily: font, fontSize: "11.5px", color: minutes > 0 ? textSecondary : accent, fontWeight: 600 }}>
              {minutes > 0 ? `${minutes}m` : "—"}
            </Typography>
          </Box>
        </Row>

        {wantsPeople && (
          <Row icon={<PeopleIcon />} align="flex-start">
            <Autocomplete
              multiple={multi}
              size="small"
              options={students}
              value={multi ? invitees : (invitees[0] || null)}
              getOptionLabel={(s) => `${s.first_name} ${s.last_name}`}
              isOptionEqualToValue={(a, b) => String(a._id) === String(b._id)}
              onChange={(e, v) => setInvitees(multi ? v : (v ? [v] : []))}
              renderTags={(value, getTagProps) =>
                value.map((s, i) => (
                  <Chip
                    size="small"
                    label={`${s.first_name} ${s.last_name}`}
                    {...getTagProps({ index: i })}
                    key={String(s._id)}
                    sx={{ fontFamily: font, fontSize: "11px" }}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={
                    kind === "class" ? "Invite students…"
                      : kind === "reserve" ? "Held for…"
                        : "Offer it to…"
                  }
                  sx={inputSx}
                />
              )}
            />
            {kind === "class" && (
              <Typography sx={{ fontFamily: font, fontSize: "10.5px", color: textMuted, mt: 0.5 }}>
                {invitees.length
                  ? `${invitees.length} invited · ${Math.max(0, Number(capacity) - invitees.length)} seats left for anyone else`
                  : "Leave empty and students can find and join it themselves."}
              </Typography>
            )}
          </Row>
        )}

        {(kind === "class" || kind === "oneToOne") && (
          <Row icon={<MenuBookIcon />}>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              {kind === "class" && (
                <TextField
                  select size="small" label="Lesson" value={lessonNumber}
                  onChange={(e) => pickLesson(e.target.value)}
                  sx={{ ...inputSx, flex: "1 1 150px" }}
                >
                  {lessons.map((l) => (
                    <MenuItem key={l.n} value={l.n} sx={{ fontFamily: font, fontSize: "12.5px" }}>
                      {l.n}. {l.title}{l.group ? " ★" : ""}
                    </MenuItem>
                  ))}
                </TextField>
              )}
              <TextField
                select size="small" label="Subject" value={subject}
                onChange={(e) => setSubject(e.target.value)}
                sx={{ ...inputSx, flex: "1 1 150px" }}
              >
                <MenuItem value="" sx={{ fontFamily: font, fontSize: "12.5px" }}>None</MenuItem>
                {subjectOptions.map((s) => (
                  <MenuItem key={s} value={s} sx={{ fontFamily: font, fontSize: "12.5px" }}>
                    {formatSubjectName(s)}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
            {kind === "class" && (
              <Typography sx={{ fontFamily: font, fontSize: "10.5px", color: textMuted, mt: 0.5 }}>
                ★ marks the four discussion-shaped lessons a class is really for.
              </Typography>
            )}
          </Row>
        )}

        {kind === "class" && (
          <>
            <Row icon={<PaymentsIcon />}>
              <Box sx={{ display: "flex", gap: 1 }}>
                <TextField
                  size="small" type="number" label={`Price per head (${cur})`} value={price}
                  onChange={(e) => setPrice(e.target.value)} sx={{ ...inputSx, flex: 1 }}
                />
                <TextField
                  size="small" type="number" label="Capacity" value={capacity}
                  onChange={(e) => setCapacity(e.target.value)} sx={{ ...inputSx, width: 108 }}
                />
              </Box>
              <Typography sx={{ fontFamily: font, fontSize: "10.5px", color: textMuted, mt: 0.5 }}>
                {`Full house is ${cur}${(Number(price) || 0) * (Number(capacity) || 0)} against one instructor hour.`}
              </Typography>
            </Row>

            <Row icon={<RepeatIcon />}>
              <TextField
                select size="small" label="Repeat" value={repeatWeeks}
                onChange={(e) => setRepeatWeeks(e.target.value)} sx={{ ...inputSx, width: "100%" }}
              >
                {[1, 2, 4, 6, 8, 12].map((n) => (
                  <MenuItem key={n} value={n} sx={{ fontFamily: font, fontSize: "12.5px" }}>
                    {n === 1 ? "Does not repeat" : `Weekly, ${n} weeks`}
                  </MenuItem>
                ))}
              </TextField>
              {Number(repeatWeeks) > 1 && (
                <Typography sx={{ fontFamily: font, fontSize: "10.5px", color: textMuted, mt: 0.5 }}>
                  Only the first week is filled with the students you invited — the rest are theirs to opt into.
                </Typography>
              )}
            </Row>
          </>
        )}

        {kind === "oneToOne" && (
          <Row icon={<NotesIcon />} align="flex-start">
            <TextField
              size="small" fullWidth multiline minRows={2}
              label="Why you are offering this"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              sx={inputSx}
            />
          </Row>
        )}

        {error && (
          <Typography sx={{ fontFamily: font, fontSize: "12px", color: accent, fontWeight: 600, mt: 0.5 }}>
            {error}
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2, pb: 1.75 }}>
        <Button onClick={onClose} disabled={busy} sx={secondaryButtonSx}>Cancel</Button>
        <Button onClick={submit} disabled={busy} sx={primaryButtonSx}>
          {busy ? <CircularProgress size={15} sx={{ color: "#fff" }} />
            : kind === "close" ? "Close this time"
              : kind === "reserve" ? "Reserve"
                : kind === "oneToOne" ? "Offer it"
                  : "Schedule"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
